import { CircularProgress, Grid, Box } from "@mui/material";
import {
  AmountBox,
  AppAmountBoxWrapper,
  FilterNonIdeal,
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
} from "../../../common";
import { AppPagination, UBButton } from "../../../../Libraries";
import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { PlayIcon } from "../../../../assets/icons/icon";
import { useCustomerTxns } from "../../../../hooks/useAccount/customerTxns";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import AccountDetailTableHeader from "./AccountDetailTableHeader";
import ItemList from "./ItemList";
import {
  calculateTotalOutstanding,
  getCustomerByID,
} from "../../../../database/helpers/customer";
import withObservables from "@nozbe/with-observables";
import { formatNum } from "../../../../oscar-pos-core/constants";
import {
  DEFAULT_DATE_FILTERS_EXPORT,
  NEW_SORT,
  PaymentMode,
  TABLE_FILTER_ACCOUNT,
  TABLE_FILTER_ACCOUNT_WITHOUT_KEYWORDS,
} from "../../../../constants";
import { Sync } from "../../../../SyncService";
import { useAuthContext } from "../../../../context/AuthContext";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { useSnackbar } from "../../../../context/SnackbarContext";
import useDownlaodPdf from "../../../../hooks/useDownlaodPdf";
import {
  bulkDeleteOrders,
  bulkRestoreStock,
} from "../../../../database/helpers/orders";
import { bulkDeleteInventory } from "../../../../database/helpers/inventory";
import {
  buildFiltersConfig,
  dateFormatInNumberWithSpace,
  formatToISOString,
  handleShareDetails,
} from "../../../../utils";
import { useFiltersHandler } from "../../../../hooks/useFilterHandler";
import NonIdealNew from "../../../common/NonIdealNew";
import ConfirmationModal from "../../../common/Modals/ConfirmationModal";
import AppTableFilters from "../../../common/AppTableFilters";
import { isDemoMode } from "../../../../utils/demoEnviornment";
import { useWalletContext } from "../../../../context/WalletContext";
import moment from "moment";

const EnhancementAccountDetail = ({ customerId, customer }) => {
  customer = customer.length ? customer[0] : customer;

  /** ------------------ Hooks & Context ------------------ */
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const contact = searchParams.get("contact");
  const isCommunity = type?.toLowerCase() === "community";

  const [I18n] = useContext(LanguageContext);
  const { user, business } = useAuthContext();
  const { showSnackbar } = useSnackbar();
  const { onDownload, downloadLoading } = useDownlaodPdf(user);
  const [pageSize, setPageSize] = useState(0);
  const [txnsDetail, setTxnsDetail] = useState(null);
  const [openTransactionSidebar, setOpenTransactionSidebar] = useState(false);
  const [balance, setBalance] = useState({});
  const [orders, setOrders] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [isFetchingRef, setIsFetchingRef] = useState(false);

  const {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isAppliedFilter,
    isResetFilter,
    setIsResetFilter,
  } = useFiltersHandler(undefined, I18n);

  const {
    txnsList,
    allTxnslist,
    handleGetCustomerTxns,
    isLoading,
    allItemsCount,
    totalAmount,
    invalidateBalanceCache,
  } = useCustomerTxns(filters, type);

  const { accountInfo, fetchAccountInfo, isAccountLoading, EMIVerified } =
    useWalletContext();

  const [collectionDate, setCollectionDate] = useState(
    customer.total_outstanding_payment < 0 && customer.payment_at
      ? dateFormatInNumberWithSpace(customer.payment_at)
      : customer.total_outstanding_payment > 0 && customer.collect_at
        ? dateFormatInNumberWithSpace(customer.collect_at)
        : null,
  );

  /** ------------------ Effects ------------------ */

  useEffect(() => {
    handleApiCall();
  }, [params, filters]);

  useEffect(() => {
    if (allTxnslist?.length) calculateBalance();
  }, [allTxnslist]);

  useEffect(() => {
    if (location.state?.showDemoModal && isDemoMode()) {
      const timer = setTimeout(() => setShowDemoModal(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  /** ------------------ API & Helpers ------------------ */
  const handleApiCall = async () => {
    setIsFetchingRef(true);
    await handleGetCustomerTxns({
      customerId: customerId || "",
      startPage: 0,
      endPage: 20,
      offset: 0,
    });
    setIsFetchingRef(false);
  };

  const calculateBalance = () => {
    let balanceMap = {};
    let count = 0;
    const list =
      filters.sort !== NEW_SORT.Oldest
        ? [...allTxnslist].reverse()
        : allTxnslist;

    list.forEach((txn) => {
      count +=
        txn.payment_mode === "Jama" ? -Number(txn.amount) : Number(txn.amount);
      balanceMap[txn.id] = count;
    });

    setBalance(balanceMap);
  };

  const handleOpenModal = (orders) => {
    triggerLogEvent(analytics, "acc_txn_delete_btn");
    setOrders(orders);
    setDeleteConfirmation(true);
  };

  const handleCloseModal = () => setDeleteConfirmation(false);

  const handleDeleteTransaction = async () => {
    if (orders.length) {
      const toDelete = await Promise.all(
        orders.map(async (item) => ({
          original: item,
          updated: await item.stock.fetch(),
        })),
      );
      if (toDelete.length) {
        const restoreInvType = txnsDetail.payment_mode === "Udhaar" ? "in" : "out";
        try {
          await bulkDeleteOrders(toDelete);
          await bulkDeleteInventory(txnsDetail, toDelete, restoreInvType, "payment");
          await bulkRestoreStock(toDelete, restoreInvType);
        } catch (error) {
          showSnackbar({
            message: error?.response?.data?.message || I18n.something_went_wrong,
            severity: "error",
          });
          return;
        }
      }
    }

    await txnsDetail.markAsDeleted();
    const total = await calculateTotalOutstanding(customer);
    await customer.updateTotalOutstanding(total);
    triggerLogEvent(analytics, "acc_txn_deleted");
    setOpenTransactionSidebar(false);
    showSnackbar({ message: I18n.transaction_deleted, severity: "success" });
    invalidateBalanceCache();
    handleGetCustomerTxns({
      customerId: params?.id || "",
      startPage: 0,
      endPage: pageSize + 20,
    });
    setOrders([]);
    Sync(user.phone_number, navigate);
    setDeleteConfirmation(false);
  };

  /** ------------------ Action Handlers ------------------ */
  const handleEdit = () => {
    triggerLogEvent(analytics, "acc_edit_btn");
    navigate(`/Accounts/ContactForm/Edit/${customerId}`, {
      state: { isFromAccountDetail: true },
    });
  };

  const handleTook = () => {
    triggerLogEvent(
      analytics,
      customer.is_supplier ? "acc_supplier_take" : "acc_customer_take",
    );
    navigate(`/Accounts/TransactionForm/${customerId}`, {
      state: { paymentMode: PaymentMode.Jama },
    });
  };

  const handleGave = () => {
    triggerLogEvent(
      analytics,
      customer.is_supplier ? "acc_supplier_give" : "acc_customer_give",
    );
    navigate(`/Accounts/TransactionForm/${customerId}`, {
      state: { paymentMode: PaymentMode.Udhaar },
    });
  };

  const handleDate = async (date) => {
    setCollectionDate(date ? dateFormatInNumberWithSpace(date) : null);
    if (customer.total_outstanding_payment < 0) {
      await customer.updatePaymentDate(date);
    } else {
      await customer.updateCollectionDate(date);
    }
    Sync(user.phone_number, navigate);
    showSnackbar({
      message: date
        ? customer.total_outstanding_payment < 0
          ? I18n.due_date_set
          : I18n.collection_date_set
        : customer.total_outstanding_payment < 0
          ? I18n.due_date_remove
          : I18n.collection_date_remove,
      severity: "success",
    });
  };

  const handleSendReminder = async () => {

    triggerLogEvent(analytics, "acc_send_reminder");

    let info = accountInfo;

    if (!info && user?.phone_number) {
      info = await fetchAccountInfo(navigate);
    }

    handleShareDetails(
      business.name,
      info,
      null,
      navigate,
      false,
      "accounts_reminder_share_details",
      customer,
      Math.abs(Math.round(customer.total_outstanding_payment)),
    );
  };

  const handleDownload = async (startDate, endDate) => {
    const paramsForReport = {
      name: `${customer.name.toLowerCase()}_mini_statement.pdf`,
      template_name: "accountreport.hbs",
      customer_id: customer.id,
      type: isCommunity ? "shared-mini" : "mini",
      pdf: true,
      supplier: isCommunity ? false : customer.is_supplier,
      ...(startDate &&
        endDate && {
        start_date: formatToISOString(startDate),
        end_date: formatToISOString(endDate),
      }),
    };

    triggerLogEvent(analytics, "acc_download_pdf");
    onDownload(paramsForReport, false, navigate);
  };

  /** ------------------ Computed Data ------------------ */
  const isNonIdeal = txnsList.length === 0 && !isAppliedFilter;

  const isCommunityNonIdeal =
    txnsList.length == 0 &&
    !isAppliedFilter &&
    !isLoading &&
    isCommunity &&
    !isFetchingRef;

  const amountData = useMemo(() => {
    const amt = isCommunity ? totalAmount : customer?.total_outstanding_payment;
    const title =
      amt === 0
        ? I18n.account_settled
        : amt > 0
          ? isCommunity
            ? I18n.i_will_give
            : I18n.i_will_receive
          : isCommunity
            ? I18n.i_will_receive
            : I18n.i_will_give;
    const colorClass =
      amt < 0
        ? isCommunity
          ? "foundationOrangeColor"
          : "foundationGreenColor"
        : amt > 0
          ? isCommunity
            ? "foundationGreenColor"
            : "foundationOrangeColor"
          : "newFontColor";

    return [
      {
        amount: formatNum(Math.abs(amt)) || 0,
        title,
        id: title,
        rsLabel: true,
        amountParaTextClassname: colorClass,
      },
    ];
  }, [customer?.total_outstanding_payment, I18n, totalAmount]);

  const isDateSort =
    filters.sort === NEW_SORT.Newest ||
    filters.sort === NEW_SORT.Oldest ||
    filters.sort == null ||
    !filters.sort;
  const isAscendingDate = isDateSort && filters.sort !== NEW_SORT.Oldest;

  const isAscendingDetails = filters.sort === NEW_SORT.nameAToZ;

  const handleTableHeaderSort = (column) => {
    if (column === "date") {
      const isDate =
        filters.sort === NEW_SORT.Newest ||
        filters.sort === NEW_SORT.Oldest ||
        filters.sort == null ||
        !filters.sort;
      if (isDate) {
        handleFilterChange(
          "sort",
          filters.sort === NEW_SORT.Oldest ? NEW_SORT.Newest : NEW_SORT.Oldest,
        );
      } else {
        handleFilterChange("sort", NEW_SORT.Newest);
      }
    } else if (column === "details") {
      handleFilterChange(
        "sort",
        filters.sort === NEW_SORT.nameAToZ
          ? NEW_SORT.nameZToA
          : NEW_SORT.nameAToZ,
      );
    }
  };

  /** ------------------ Render ------------------ */
  return (
    <MainContainer fluid className="accountPage quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              id: "accountDetail",
              title: isCommunity ? contact : customer.name,
              isTitleCase: false,
              isBack: true,
              isEdit: !isCommunity,
              onEdit: handleEdit,
              onBack: () =>
                location.state?.backTo
                  ? navigate(-1)
                  : navigate("/Accounts", {
                    state: { isSupplier: customer.is_supplier, isCommunity },
                  }),
            }}
          >
            {!isCommunity && (
              <>
                <UBButton
                  onClick={handleTook}
                  id="addTransactionButton"
                  className="ubPrimaryButton fontSize12 fontWeight400 textUppercase marginLeft12"
                  title={I18n.itook}
                />
                <UBButton
                  onClick={handleGave}
                  id="addTransactionButton"
                  className="ubPrimaryButton fontSize12 fontWeight400 textUppercase marginLeft12"
                  title={I18n.igave}
                />
              </>
            )}
          </TitleAndActionBox>

          <InnerWrapper className="innerWrapper newInnerWrapper">
            {isNonIdeal && !isLoading && !isFetchingRef ? (
              isCommunity ? (
                <NonIdealNew
                  title={I18n.no_transactions_yet}
                  subtitle={
                    I18n.transaction_added_by_this_contact_will_appear_here_automatically
                  }
                />
              ) : (
                <NonIdealNew
                  title={
                    // Urdu/Sindhi are RTL: force direction so a Latin name stays on the right
                    <span dir={["ur", "snd"].includes(I18n.lang) ? "rtl" : "ltr"}>
                      {(I18n.add_first_transaction_for_name || "Add first transaction for {name}").replace("{name}", customer.name || contact)}
                    </span>
                  }
                  subtitle={
                    I18n.track_the_matter_of_you_took_money_or_gave_money
                  }
                  button1Title="itook"
                  onButton1Click={handleTook}
                  loading={false}
                  disabledOne={false}
                  buttonOneId="tookButton"
                  prefixIconButtonTwo={
                    <PlayIcon
                      width="18px"
                      height="18px"
                      fill="var(--blue500)"
                    />
                  }
                  listButton2Title="igave"
                  onListButton2Click={handleGave}
                  listButtonTwoId="gaveButton"
                />
              )
            ) : isCommunityNonIdeal ? (
              <NonIdealNew
                title={`No transaction found for ${customer.name || contact}`}
                subtitle={"There is no transaction related to this contact"}
              />
            ) : (
              <Fragment>
                <Box className="AccountWrapper">
                  <AppTableFilters
                    filters={
                      isCommunity
                        ? TABLE_FILTER_ACCOUNT_WITHOUT_KEYWORDS
                        : TABLE_FILTER_ACCOUNT
                    }
                    onFilterChange={handleFilterChange}
                    appliedChips={appliedChips}
                    filterState={filters}
                    isResetFilter={isResetFilter}
                    setIsResetFilter={setIsResetFilter}
                    onRemoveChip={(chip) => {
                      setIsFetchingRef(true);
                      handleRemoveChip(chip);
                    }}
                    filtersConfig={buildFiltersConfig(isCommunity
                        ? TABLE_FILTER_ACCOUNT_WITHOUT_KEYWORDS
                        : TABLE_FILTER_ACCOUNT, [], [], [
                        { label: I18n.received, value: "Received" },
                        { label: I18n.given, value: "Given" },
                      ], "Search by notes...", null, null, null, null, I18n)}
                    exportConfig={
                      // Mini statement export isn't supported for community (shared) accounts
                      isCommunity
                        ? null
                        : {
                          onExport: handleDownload,
                          loading: downloadLoading,
                          dateFilters: DEFAULT_DATE_FILTERS_EXPORT,
                          applyBtnText: I18n.export,
                          spinnerColor: "newFontColor",
                        }
                    }
                    dueDateConfig={
                      isCommunity
                        ? null
                        : {
                          onExport: handleDate,
                          loading: false,
                          applyBtnText: collectionDate
                            ? collectionDate
                            : I18n.due_date,
                          spinnerColor: "newFontColor",
                          selectedDate: collectionDate,
                        }
                    }
                    sendReminderConfig={
                      !EMIVerified || isCommunity
                        ? null
                        : collectionDate &&
                          customer.total_outstanding_payment > 0
                          ? {
                            onExport: handleSendReminder,
                            loading: isAccountLoading,
                            applyBtnText: I18n.send_reminder,
                          }
                          : null
                    }
                  />

                  <AppAmountBoxWrapper>
                    <AmountBox data={amountData} />
                  </AppAmountBoxWrapper>

                  <AccountDetailTableHeader
                    isAscending={isAscendingDate}
                    isAscendingDetails={isAscendingDetails}
                    isAppliedFilter={isAppliedFilter}
                    handleSort={handleTableHeaderSort}
                    disabled={!txnsList?.length}
                  />

                  <Box
                    className="accountListBodyWarpper generic-scrollbar"
                    id="scrollableDiv"
                  >
                    {isLoading &&
                      (txnsList?.length === 0 || isFetchingRef) &&
                      isCommunity ? (
                      <Box
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "100%" }}
                      >
                        <CircularProgress size="30px" />
                      </Box>
                    ) : txnsList?.length ? (
                      <AppPagination
                        data={{
                          handleNext: () => {
                            if (txnsList?.length < allItemsCount) {
                              handleGetCustomerTxns({
                                customerId: params?.id || "",
                                startPage: 0,
                                endPage: 20 + (pageSize + 20),
                                offset: txnsList?.length,
                              });
                              setPageSize((value) => value + 20);
                            }
                          },
                          pageLoad: isLoading,
                          count: allItemsCount,
                          list: txnsList,
                          scrollableTarget: "scrollableDiv",
                        }}
                      >
                        {txnsList.map((item, index) => (
                          <ItemList
                            key={index}
                            item={
                              isCommunity
                                ? {
                                  ...item,
                                  amount: item?.given || item?.received,
                                  payment_mode:
                                    item?.given > 0 ? "Udhaar" : "Jama",
                                  transacted_at: item?.date,
                                }
                                : item
                            }
                            onSelect={setTxnsDetail}
                            type={type}
                            txnsDetail={txnsDetail}
                            balance={balance}
                            index={index}
                            handleOpenModal={handleOpenModal}
                            customer={customer}
                            openTransactionSidebar={openTransactionSidebar}
                            setOpenTransactionSidebar={
                              setOpenTransactionSidebar
                            }
                            isAppliedFilter={isAppliedFilter}
                          />
                        ))}
                      </AppPagination>
                    ) : !isLoading ? (
                      <Box className="h-100">
                        <FilterNonIdeal
                          title={I18n.no_transactions_matching_those_filters}
                          subTitle={I18n.try_adjusting_filters}
                          subTitleTextOnClick={() => {
                            setIsFetchingRef(true);
                            resetFilters();
                          }}
                          subTitleText={I18n.reset_small}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </Fragment>
            )}
          </InnerWrapper>
        </Grid>
      </Grid>

      <ConfirmationModal
        title={I18n.are_you_sure_you_want_to_delete_transaction}
        message={I18n.this_action_cannot_be_undone}
        confirmText={I18n.delete}
        open={deleteConfirmation}
        onConfirm={handleDeleteTransaction}
        onClose={handleCloseModal}
        id="account_detail_delete_transaction_modal"
      />
    </MainContainer>
  );
};

const enhance = withObservables(["customerId"], ({ customerId }) => ({
  customer: getCustomerByID(customerId),
}));

export default enhance(EnhancementAccountDetail);
